import { all, call, put, select, takeLatest, takeEvery, delay } from 'redux-saga/effects';
import { isApiError, requestPuppy } from '@wix/da-http-client';
import ddt from '@wix/da-ddt';
import { logBiEvent } from '@wix/da-shared-react/pkg/biLogger/redux/saga';
import { BiData, VideoCreatorCreateButtonClickBiEvent } from '@wix/da-bi/pkg/events';
import { putEntities } from '@wix/da-shared-react/pkg/redux/entities/actions';
import { withOffset, selectors as streamSelectors } from '@wix/da-shared-react/pkg/Stream';
import { actions as notificationActions } from '@wix/da-shared-react/pkg/Notifications';
import { NotificationType } from '../../types/notifications';
import { fetchStreamContents, takeStreamFetch } from '@wix/da-shared-react/pkg/Stream/helpers';
import { STUDIO_VIDEO_GENERATIONS_STREAM } from '../../../constants';
import {
  generateVideo,
  updateVideoCreatorStatus,
  updateVideoCreatorErrorCode,
  updateVideoCreatorRestrictions,
  setFirstGenModalSeen,
  fetchGenerationCost,
  updateVideoCreatorPricePoints,
  updateVideoCreatorLowestPricePoints,
  updateVideoCreatorGenerationCostModel,
  updateVideoCreatorModel,
  updateVideoCreatorReferenceDeviation,
  updateVideoCreatorAspectRatio,
  updateVideoCreatorDuration,
  updateVideoCreatorSound,
  updateVideoCreatorIsCalculatingCost,
  addVideoCreatorPendingGeneration,
  removeVideoCreatorPendingGeneration,
  initializeVideoCreator,
  fetchNextVideoGenerations,
  FETCH_NEXT_VIDEO_GENERATIONS_TYPE,
  saveVideo,
} from '../../actions/studio/videoCreator';
import { receiveHubMessage } from '@wix/da-shared-react/pkg/redux/hub/actions';
import { HubChannel } from '@wix/da-shared-react/pkg/redux/hub/types';
import { UploadErrorCode } from '@wix/da-shared-react/pkg/redux/uploads/types';
import { setCompliantLocalStorageItem } from '@wix/da-react-context/pkg/compliantLocalStorage/redux/actions';
import { CompliantLocalStorageKeys } from '@wix/da-react-context/pkg/compliantLocalStorage/redux/constants';
import {
  PapiRequestDreamsofartGenerateVideo,
  PapiDreamsofartGenerateVideo,
  PapiRequestDreamsofartVideos,
  PapiDreamsofartVideos,
  PapiRequestDreamsofartSaveVideo,
  PapiDreamsofartSaveVideo,
  PapiVideoCreatorGeneration,
  PapiError,
  PapiDeviation,
} from '@wix/da-papi-types';
import DeviationMediaService from '@wix/da-shared-react/pkg/utils/DeviationMediaService';
import {
  getVideoCreatorPrompt,
  getVideoCreatorModel,
  getVideoCreatorAspectRatio,
  getVideoCreatorDuration,
  getVideoCreatorSound,
  getVideoCreatorReferenceDeviation,
  getVideoCreatorPendingGenerations,
} from '../../selectors/studio/videoCreator';
import {
  VideoAspectRatio,
  VideoCreatorErrorCode,
  VideoCreatorPendingGeneration,
  VideoModel,
} from '../../types/store';
import { getModelPropertiesByType } from '../../pages/VideoCreator/ModelProperties';

const DEBOUNCE_DELAY = 300;

function pickClosestAspectRatioForModelAndDeviation(
  model: VideoModel,
  deviation: PapiDeviation
): VideoAspectRatio | undefined {
  const modelProps = getModelPropertiesByType(model);
  const candidates = modelProps?.availableAspectRatios ?? [];
  if (candidates.length === 0) {
    return undefined;
  }

  const fullview = DeviationMediaService.getLargestFullview(deviation);
  const w = fullview.width;
  const h = fullview.height;
  if (w == null || h == null || w <= 0 || h <= 0) {
    return undefined;
  }

  const deviationAspect = w / h;
  let best: VideoAspectRatio | undefined;
  let bestDist = Infinity;

  for (const candidate of candidates) {
    const [candidateWidth, candidateHeight] = candidate.split(':').map(Number);
    const candidateAspect = candidateWidth / candidateHeight;
    if (!Number.isFinite(candidateAspect)) {
      continue;
    }
    const dist = Math.abs(Math.log(candidateAspect / deviationAspect));
    if (dist < bestDist) {
      bestDist = dist;
      best = candidate;
    }
  }

  return best;
}

function* handleGenerateVideo() {
  ddt.log('videoCreator', 'handleGenerateVideo called');
  const prompt: string = yield select(getVideoCreatorPrompt);
  const aspectRatio: string = yield select(getVideoCreatorAspectRatio);
  const duration: number = yield select(getVideoCreatorDuration);
  const sound: boolean = yield select(getVideoCreatorSound);
  const referenceDeviation = yield select(getVideoCreatorReferenceDeviation);
  const selectedModel = yield select(getVideoCreatorModel);

  ddt.log('videoCreator', 'handleGenerateVideo - collected state', {
    prompt,
    aspectRatio,
    duration,
    sound,
    selectedModel,
  });

  const model = getModelPropertiesByType(selectedModel);

  const requestData: PapiRequestDreamsofartGenerateVideo = {
    prompt,
    model_id: model?.id,
    aspect_ratio: aspectRatio,
    duration,
    sound,
  };
  if (referenceDeviation) {
    requestData.reference_deviationid = referenceDeviation?.deviationId;
  }

  yield put(updateVideoCreatorStatus('busy'));

  const response: PapiDreamsofartGenerateVideo | PapiError = yield call(
    requestPuppy,
    {
      url: 'generate_video',
      method: 'post',
      data: requestData,
    },
    () => ({
      success: true,
      generationId: 5,
      restrictions: {
        pointsBalance: 912,
      },
    }),
    'dreamsofart'
  );

  if (!response || isApiError(response) || response.generationId === undefined) {
    ddt.error('videoCreator', 'Failed to generate video', response);
    yield put(updateVideoCreatorStatus('idle'));

    const { errorCode: apiErrorCode } = response as PapiError;
    let mappedErrorCode: VideoCreatorErrorCode;
    let biErrorString = undefined;

    // Safety check: if backend returns our magic NO_ERROR number, alert
    if (apiErrorCode === VideoCreatorErrorCode.NO_ERROR) {
      ddt.alert('videoCreator', "Backend using FED's magic no_error number", ['error'], {
        apiErrorCode,
        response,
      });
      mappedErrorCode = VideoCreatorErrorCode.OTHER;
      biErrorString = 'unknown_error';
    } else if (
      apiErrorCode !== undefined &&
      typeof apiErrorCode === 'number' &&
      Object.values(VideoCreatorErrorCode).includes(apiErrorCode) &&
      // Who in the world decided to use errorCode 0 as an actual error code?
      (apiErrorCode !== 0 || isApiError(response))
    ) {
      mappedErrorCode = apiErrorCode as VideoCreatorErrorCode;
      switch (mappedErrorCode) {
        case VideoCreatorErrorCode.VALIDATION_ERROR:
          biErrorString = 'validation_error';
          break;
        case VideoCreatorErrorCode.UPLOAD_ERROR:
          biErrorString = 'upload_error';
          break;
        case VideoCreatorErrorCode.UPLOADS_TEMPORARILY_DISABLED:
          biErrorString = 'uploads_disabled';
          break;
        case VideoCreatorErrorCode.ILLEGAL_PROMPT:
          biErrorString = 'illegal_prompt';
          break;
        case VideoCreatorErrorCode.INSUFFICIENT_POINTS_BALANCE:
          biErrorString = 'insufficient_points';
          break;
        case VideoCreatorErrorCode.OUT_OF_SPACE:
          biErrorString = 'out_of_space';
          break;
        case VideoCreatorErrorCode.OUT_OF_FILE_QUOTA:
          biErrorString = 'out_of_file_quota';
          break;
        case VideoCreatorErrorCode.TOO_FAST:
          biErrorString = 'velocity';
          break;
        case VideoCreatorErrorCode.TOS_VIOLATION:
          biErrorString = 'tos_violation';
          break;
        case VideoCreatorErrorCode.OTHER:
        default:
          biErrorString = 'unknown_error';
      }
    } else if (apiErrorCode === UploadErrorCode.UPLOADS_TEMPORARILY_DISABLED) {
      mappedErrorCode = VideoCreatorErrorCode.UPLOADS_TEMPORARILY_DISABLED;
      biErrorString = 'uploads_disabled';
    } else {
      mappedErrorCode = VideoCreatorErrorCode.OTHER;
    }
    yield call(
      logBiEvent,
      BiData<VideoCreatorCreateButtonClickBiEvent>({
        evid: 950,
        prompt,
        is_valid: false,
        generationid: undefined,
        ai_model: model?.id ?? '',
        error: biErrorString,
      })
    );
    yield put(updateVideoCreatorErrorCode(mappedErrorCode));
  } else {
    const pendingGeneration: VideoCreatorPendingGeneration = {
      generationId: response.generationId,
      modelType: selectedModel,
      prompt,
      aspectRatio,
      duration,
      referenceDeviation,
      sound,
    };
    yield call(
      logBiEvent,
      BiData<VideoCreatorCreateButtonClickBiEvent>({
        evid: 950,
        prompt,
        is_valid: true,
        generationid: response.generationId,
        ai_model: model?.id ?? '',
      })
    );
    yield all([
      put(updateVideoCreatorRestrictions(response.restrictions)),
      put(updateVideoCreatorErrorCode(VideoCreatorErrorCode.NO_ERROR)),
      put(addVideoCreatorPendingGeneration(pendingGeneration)),
      put(updateVideoCreatorStatus('idle')),
    ]);
    ddt.info('videoCreator', 'Video generation initiated successfully', {
      generationId: response.generationId,
    });
  }
}

function* handleSetFirstGenModalSeen() {
  yield put(
    setCompliantLocalStorageItem({
      key: CompliantLocalStorageKeys.VIDEO_CREATOR_FIRST_GEN_SEEN,
      value: '1',
    })
  );
}

function* handleFetchGenerationCost({
  payload: { modelId, duration, sound },
}: ReturnType<typeof fetchGenerationCost>) {
  yield put(updateVideoCreatorIsCalculatingCost(true));

  const requestData = {
    model_id: modelId,
    duration,
    sound,
  };

  const response: { pricePoints?: number; lowestPricePoints?: number } = yield call(
    requestPuppy,
    {
      url: 'generation_cost',
      method: 'post',
      data: requestData,
    },
    () => ({
      pricePoints: 100,
      lowestPricePoints: 14,
    }),
    'dreamsofart'
  );

  // Extract pricePoints and lowestPricePoints from response object
  const pricePoints: number | undefined =
    response && typeof response === 'object' && !isApiError(response)
      ? response.pricePoints
      : undefined;
  const lowestPricePoints: number | undefined =
    response && typeof response === 'object' && !isApiError(response)
      ? response.lowestPricePoints
      : undefined;

  if (
    !response ||
    isApiError(response) ||
    (response as any)?.errorCode !== undefined ||
    pricePoints === undefined
  ) {
    ddt.error('videoCreator', 'Failed to fetch generation cost', {
      response,
      responseType: typeof response,
      pricePoints,
    });
    yield put(updateVideoCreatorIsCalculatingCost(false));
  } else {
    yield put(updateVideoCreatorPricePoints(pricePoints));
    if (lowestPricePoints !== undefined) {
      yield put(updateVideoCreatorLowestPricePoints(lowestPricePoints));
    }
    yield put(updateVideoCreatorGenerationCostModel(modelId));
    yield put(updateVideoCreatorIsCalculatingCost(false));
    ddt.info('videoCreator', 'Generation cost fetched successfully', {
      pricePoints,
      lowestPricePoints,
    });
  }
}

function* syncAspectRatioWhenModelChangesWithStartingImage() {
  const referenceDeviation = yield select(getVideoCreatorReferenceDeviation);
  if (!referenceDeviation) {
    return;
  }

  const model = yield select(getVideoCreatorModel);
  const closest = pickClosestAspectRatioForModelAndDeviation(model, referenceDeviation);
  if (closest !== undefined) {
    yield put(updateVideoCreatorAspectRatio(closest));
  }
}

function* syncAspectRatioWhenReferenceDeviationSet(
  action: ReturnType<typeof updateVideoCreatorReferenceDeviation>
) {
  const deviation = action.payload.referenceDeviation;
  if (!deviation) {
    return;
  }

  const model = yield select(getVideoCreatorModel);
  const closest = pickClosestAspectRatioForModelAndDeviation(model, deviation);
  if (closest !== undefined) {
    yield put(updateVideoCreatorAspectRatio(closest));
  }
}

function* handleVideoCreatorPropertyChange() {
  yield delay(DEBOUNCE_DELAY);

  const selectedModel = yield select(getVideoCreatorModel);
  const duration: number = yield select(getVideoCreatorDuration);
  const sound: boolean = yield select(getVideoCreatorSound);

  // Convert model type to model ID
  const model = getModelPropertiesByType(selectedModel);
  if (!model?.id) {
    ddt.warn('videoCreator', 'Cannot fetch generation cost: model ID not found');
    return;
  }

  // Dispatch the fetch action with current values
  yield put(fetchGenerationCost(model.id, duration, sound));
}

function* handleInitializeVideoCreator(action: ReturnType<typeof initializeVideoCreator>) {
  const { videos } = action.payload.initData;

  if (!videos || !videos.results) {
    ddt.warn('No videos in init data', { videos });
    return;
  }

  ddt.log('Initializing video creator with videos', {
    count: videos.results.length,
    generationIds: videos.results.map(v => v.generationId),
  });

  // Store videos in stream using generationId as the item identifier
  const videoItems = videos.results.map(video => video.generationId);

  // Store video objects in entities for easy access
  const videoEntities = {
    videoGeneration: videos.results.reduce(
      (acc, video) => {
        acc[video.generationId] = video;
        return acc;
      },
      {} as Record<number, (typeof videos.results)[0]>
    ),
  };

  yield put(putEntities({ entities: videoEntities }));
  yield put(
    withOffset.actions.initialize({
      streamId: STUDIO_VIDEO_GENERATIONS_STREAM.streamId,
      itemsPerFetch: 24,
      items: videoItems,
      hasMore: videos.hasMore ?? false,
      nextOffset: videos.nextOffset,
      // Required so stream saga dispatches this after generic fetchNext (takeStreamFetch → API).
      fetchNextCallback: FETCH_NEXT_VIDEO_GENERATIONS_TYPE,
    })
  );

  ddt.log('Video stream initialized', {
    streamId: STUDIO_VIDEO_GENERATIONS_STREAM.streamId,
    itemsCount: videoItems.length,
    hasMore: videos.hasMore ?? false,
  });
}

function* handleHubMessageForVideoCreator(action: ReturnType<typeof receiveHubMessage>) {
  const { channel, data } = action.payload;

  if (channel !== HubChannel.DREAMSOFART) {
    return;
  }

  const videoCreatorData = data.payload;
  if (!videoCreatorData || !videoCreatorData.generation_id) {
    return;
  }

  const generationId = parseInt(videoCreatorData.generation_id);

  const pendingGenerations: VideoCreatorPendingGeneration[] = yield select(
    getVideoCreatorPendingGenerations
  );
  const isVideoGeneration = pendingGenerations.some(gen => gen.generationId === generationId);

  if (!isVideoGeneration) {
    return;
  }

  ddt.log('videoCreator', 'Video generation succeeded via hub', {
    generationId,
    videoCreatorData: videoCreatorData,
  });

  // Find the pending generation that matches this generationId
  const pendingGeneration = pendingGenerations.find(gen => gen.generationId === generationId);

  if (!pendingGeneration) {
    ddt.error('videoCreator', 'Pending generation not found', { generationId });
    yield put(removeVideoCreatorPendingGeneration(generationId));
    return;
  }

  const model = getModelPropertiesByType(pendingGeneration.modelType);
  if (!model?.id) {
    ddt.error('videoCreator', 'Model ID not found for model type', {
      modelType: pendingGeneration.modelType,
    });
    yield put(removeVideoCreatorPendingGeneration(generationId));
    return;
  }

  // Construct the video entity from pending generation data
  const videoEntity: PapiVideoCreatorGeneration = {
    generationId: pendingGeneration.generationId,
    prompt: pendingGeneration.prompt,
    modelId: model.id,
    aspectRatio: pendingGeneration.aspectRatio,
    duration: pendingGeneration.duration,
    sound: pendingGeneration.sound,
    createdAt: new Date().toISOString(),
    status: videoCreatorData.status as 'completed' | 'errored' | 'rejected',
    referenceDeviation: pendingGeneration.referenceDeviation,
    generatedContent:
      Array.isArray(videoCreatorData.videos) && videoCreatorData.videos.length > 0
        ? videoCreatorData.videos[0]
        : undefined,
  };

  const videoEntities = {
    videoGeneration: {
      [generationId]: videoEntity,
    },
  };
  yield put(putEntities({ entities: videoEntities }));

  // Make sure the video we're inserting is not already in the stream
  const existingItems: number[] = yield select(
    streamSelectors.getItems,
    STUDIO_VIDEO_GENERATIONS_STREAM.streamId
  );
  if (!existingItems.includes(generationId)) {
    const videoItems = [generationId];
    yield put(
      withOffset.actions.insertItems({
        streamId: STUDIO_VIDEO_GENERATIONS_STREAM.streamId,
        items: videoItems,
        offset: 0,
      })
    );
  }

  yield put(removeVideoCreatorPendingGeneration(generationId));
}

function* handleFetchNextVideoGenerations({
  payload: { streamId },
}: ReturnType<typeof fetchNextVideoGenerations>) {
  const { offset } = yield select(state => ({
    offset: withOffset.selectors.getNextOffset(state, STUDIO_VIDEO_GENERATIONS_STREAM.streamId),
  }));

  const limit = 10;

  yield fetchStreamContents<PapiRequestDreamsofartVideos, PapiDreamsofartVideos>({
    streamId: STUDIO_VIDEO_GENERATIONS_STREAM.streamId,
    url: 'videos',
    params: {
      offset: offset ?? 0,
      limit,
    },
    papiProject: 'dreamsofart',
    onSuccess: function* (result) {
      // Store video objects in entities
      const videoEntities = {
        videoGeneration: result.results.reduce(
          (acc, video) => {
            acc[video.generationId] = video;
            return acc;
          },
          {} as Record<number, (typeof result.results)[0]>
        ),
      };
      yield put(putEntities({ entities: videoEntities }));
    },
    getStreamData: result => {
      // Store videos in stream using generationId as the item identifier
      const videoItems = result.results.map(video => video.generationId);
      return {
        hasMore: result.hasMore ?? false,
        items: videoItems,
        nextOffset: result.nextOffset,
      };
    },
  });
}

function* handleSaveVideo({ payload: { generationId } }: ReturnType<typeof saveVideo>) {
  ddt.log('videoCreator', 'handleSaveVideo called', { generationId });

  const requestData: PapiRequestDreamsofartSaveVideo = {
    generation_id: generationId,
  };

  const response: PapiDreamsofartSaveVideo = yield call(
    requestPuppy,
    {
      url: 'save_video',
      method: 'post',
      data: requestData,
    },
    () => ({
      deviation: {
        deviationId: 12345,
      } as any,
    }),
    'dreamsofart'
  );

  if (
    !response ||
    isApiError(response) ||
    (response as any).errorCode !== undefined ||
    !response.deviation
  ) {
    ddt.error('videoCreator', 'Failed to save video', response);
    return;
  }

  ddt.info('videoCreator', 'Video saved successfully', {
    generationId,
    deviationId: response.deviation.deviationId,
  });

  // Show success notification
  yield put(
    notificationActions.addNotification(NotificationType.SAVE_TO_DRAFTS, {
      deviation: response.deviation,
    })
  );
}

export default function* saga() {
  yield all([
    takeLatest(generateVideo, handleGenerateVideo),
    takeEvery(updateVideoCreatorModel, syncAspectRatioWhenModelChangesWithStartingImage),
    takeEvery(updateVideoCreatorReferenceDeviation, syncAspectRatioWhenReferenceDeviationSet),
    takeEvery(setFirstGenModalSeen, handleSetFirstGenModalSeen),
    takeEvery(fetchGenerationCost, handleFetchGenerationCost),
    takeEvery(initializeVideoCreator, handleInitializeVideoCreator),
    takeStreamFetch(fetchNextVideoGenerations, handleFetchNextVideoGenerations),
    takeLatest(
      [updateVideoCreatorModel, updateVideoCreatorDuration, updateVideoCreatorSound],
      handleVideoCreatorPropertyChange
    ),
    // Listen to receiveHubMessage directly to get full hub data
    takeLatest(receiveHubMessage, handleHubMessageForVideoCreator),
    takeLatest(saveVideo, handleSaveVideo),
  ]);
}
